import type { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { useUser } from '@r/api/auth.tsx';

import { Form } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Lock } from 'lucide-react';
import { Label } from '@ui/label.tsx';
import { useTranslation } from 'react-i18next';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';

const FormSchema = z.object({
  phone: z.string()
});

export const ShippingForm = ({ form }: { form: UseFormReturn<z.infer<typeof FormSchema>> }) => {
  const { t } = useTranslation();
  const user = useUser().data;
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
  const company = activeWorkspace?.company;

  if (!user) {
    return null;
  }

  if (!activeWorkspace) {
    return null;
  }

  if (!company) {
    return null;
  }

  return (
    <Form {...form}>
      <form>
        <div className="space-y-5">
          <Card className="border-none">
            <CardHeader>
              <CardTitle className="pb-4 text-lg font-semibold border-b border-gray-200">
                {t('shipping.address.title')}
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-8">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="company">{t('label.user.company')}</Label>
                    <div className="relative">
                      <Input
                        name="company"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Société"
                        type="text"
                        value={company.name}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="postalCode">{t('label.company.zipcode')}</Label>
                    <div className="relative">
                      <Input
                        name="postalCode"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Code postal"
                        value={company.zipcode}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="adress">{t('label.company.address.line1')}</Label>
                    <div className="relative">
                      <Input
                        name="adress"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Adresse postale"
                        type="adress"
                        value={company.addressLine1}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="city">{t('label.company.city')}</Label>
                    <div className="relative">
                      <Input
                        name="city"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Ville"
                        value={company.city}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="postalCode">{t('label.company.address.complement')}</Label>
                    <div className="relative">
                      <Input
                        name="adressComplement"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Complement d'adresse"
                        value={company.addressLine2 || ''}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="email">{t('label.company.country')}</Label>
                    <div className="relative">
                      <Input
                        name="country"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Pays"
                        type="text"
                        value={company.country?.name}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none">
            <CardHeader>
              <CardTitle className="pb-4 text-lg font-semibold border-b border-gray-200">
                {t('shipping.contact.details')}
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-8">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="firstName">{t('label.user.firstname')}</Label>
                    <div className="relative">
                      <Input
                        name="firstName"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Prénom"
                        value={user?.firstname}
                        type="text"
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="email">{t('label.user.email')}</Label>
                    <div className="relative">
                      <Input
                        name="email"
                        type="email"
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Email"
                        value={user?.email}
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col space-y-2">
                    <Label htmlFor="lastName">{t('label.user.lastname')}</Label>
                    <div className="relative">
                      <Input
                        name="lastName"
                        value={user?.lastname}
                        disabled
                        className="peer pe-9 disabled:opacity-100"
                        placeholder="Nom de famille"
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <div className="flex gap-1 justify-between items-center">
                      <Label htmlFor="phone">{t('label.user.mobile')}</Label>
                    </div>
                    <div className="relative">
                      <Input
                        name="phone"
                        value={activeWorkspace.phone ?? ''}
                        placeholder="Portable"
                        type="text"
                        className="peer pe-9 disabled:opacity-100"
                        disabled
                      />
                      <div className="flex absolute inset-y-0 justify-center items-center pointer-events-none end-0 pe-3 text-muted-foreground/80 peer-disabled:opacity-50">
                        <Lock size={16} strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </Form>
  );
};
